import React, { useEffect, useState, useMemo } from "react";
import { ISalary, SalaryType } from "../../types/salary.type";
import { api } from "../../api/api";
import {
  Alert,
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  message,
  DatePicker,
  Card,
  Statistic,
} from "antd";
import { DownOutlined, HistoryOutlined } from "@ant-design/icons";
import { Cashbox } from "../../types/cashbox.type";
import useGetRequest from "../../hooks/useGetRequest";
import { useNavigate } from "react-router-dom";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { Currency } from "../../types/currency.type";
import { Machine } from "../../types/machine.type";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import WorkerSalaryHistory from "../../components/WorkerSalaryHistory/workerSalaryHistory.component";
import DeleteWorkerModal from "../../components/DeleteWorkerModal/deleteWorkerModal.component";
import dayjs from "dayjs";
import { ExpenseType } from "../../types/transaction.type";
import { canEdit } from "../../utils/roleUtils";
import * as XLSX from "xlsx";

const { Title } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

function toDDMMYY(dateStr: string) {
  if (!dateStr) return "";
  const [yyyy, mm, dd] = dateStr.split("-");
  return `${dd}.${mm}.${yyyy.slice(2)}`;
}

// --- ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ДЛЯ ИТОГОВ ПО ДЕТАЛЯМ ---
function getDetailTotals(salaryReports: readonly any[], detailColumnsGrouped: any[], reportWorker: any) {
  return detailColumnsGrouped.map((detail: any) => {
    let totalQty = 0;
    let totalSum = 0;
    salaryReports.forEach((report) => {
      const d = report.details[detail.children[0].key.split('-')[1]];
      if (d) {
        const qty = (d.dayQuantity || 0) + (d.nightQuantity || 0) - (d.wasteQuantity || 0);
        const price = d.price || (reportWorker?.pricings?.[detail.children[0].key.split('-')[1]]?.perUnit || 0);
        totalQty += qty;
        totalSum += qty * price;
      }
    });
    return { qty: totalQty, sum: totalSum };
  });
}

// --- ВСПОМОГАТЕЛЬНАЯ ФУНКЦИЯ ДЛЯ ФОРМАТА ПЕРИОДА ---
function formatPeriod(range: [string, string] | null) {
  if (!range || !range[0] || !range[1]) return '';
  const [start, end] = range;
  const toHuman = (d: string) => {
    if (!d) return '';
    const [yyyy, mm, dd] = d.split('-');
    return `${dd}.${mm}.${yyyy}`;
  };
  return `Период: ${toHuman(start)} — ${toHuman(end)}`;
}

export default function Salary() {
  const { authState } = useAuth();
  const { exchangeRate } = useExchangeRate();
  const navigate = useNavigate();
  const [salaries, setSalaries] = useState<ISalary[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [ourDebtUSD, setOurDebtUSD] = useState<number>(0);
  const [employeesDebtUSD, setEmployeesDebtUSD] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [hasDebt, setHasDebt] = useState<boolean | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPerDayOpen, setIsPerDayOpen] = useState(false);
  const [isTransactionOpen, setIsTransactionOpen] = useState(false);
  const [isEditDebtPaidOpen, setIsEditDebtPaidOpen] = useState(false);
  const [isSalaryHistoryOpen, setIsSalaryHistoryOpen] = useState(false);
  const [isBonusOpen, setIsBonusOpen] = useState(false);
  const [isDeleteWorkerOpen, setIsDeleteWorkerOpen] = useState(false);
  const [forcePayment, setForcePayment] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState<ISalary | null>(null);
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  const [salaryType, setSalaryType] = useState<SalaryType | undefined>();
  const [form] = Form.useForm();
  const [perDayForm] = Form.useForm();
  const [transactionForm] = Form.useForm();
  const [editDebtPaidForm] = Form.useForm();
  const [bonusForm] = Form.useForm();
  const { data: cashboxesList, loading: cashboxesListLoading } =
    useGetRequest(`/cashbox/list`);
  const { data: machineList, loading: machineListLoading } =
    useGetRequest("/machine/list");
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [isTotalsModalOpen, setIsTotalsModalOpen] = useState(false);
  const [totalsDateRange, setTotalsDateRange] = useState<[string, string] | null>(null);
  const [totalsLoading, setTotalsLoading] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportWorker, setReportWorker] = useState<ISalary | null>(null);
  const [reportDateRange, setReportDateRange] = useState<[string, string] | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [salaryReports, setSalaryReports] = useState<any[]>([]);
  const [salaryTransactions, setSalaryTransactions] = useState<any[]>([]);
  const [yukPriceUSD, setYukPriceUSD] = useState<number>(0);
  const [yukPriceUZS, setYukPriceUZS] = useState<number>(0);
  const [isAllWorkersReportModalOpen, setIsAllWorkersReportModalOpen] = useState(false);
  const [allWorkersReportDateRange, setAllWorkersReportDateRange] = useState<[string, string] | null>(null);
  const [allWorkersReportLoading, setAllWorkersReportLoading] = useState(false);
  const [allWorkersData, setAllWorkersData] = useState<any[]>([]);
  const [allWorkersSearchTerm, setAllWorkersSearchTerm] = useState<string>("");

  // Move useMemo hooks to top level
  const salaryCurrency = useMemo(() => (reportWorker as any)?.salaryCurrency || (reportWorker as any)?.currency || 'USD', [reportWorker]);
  const yukPrice = useMemo(() => salaryCurrency === 'USD' ? yukPriceUSD : yukPriceUZS, [salaryCurrency, yukPriceUSD, yukPriceUZS]);

  // --- ВСПОМОГАТЕЛЬНЫЕ КОЛОНКИ ДЛЯ ДЕТАЛЕЙ ---
  const detailColumnsGrouped = React.useMemo(() => {
    if (!Array.isArray(salaryReports) || !salaryReports.length || !salaryReports[0].details) return [];
    // Показываем только те детали, у которых хотя бы в одном отчете quantity > 0
    const shownDetails = (salaryReports[0].details as any[])
      .map((detail: any, idx: number) => {
        const hasQty = salaryReports.some((r: any) => {
          const d = r.details[idx];
          return d && ((d.dayQuantity || 0) + (d.nightQuantity || 0) - (d.wasteQuantity || 0)) !== 0;
        });
        return hasQty ? { ...detail, idx } : null;
      })
      .filter(Boolean);
    // Для каждой детали — группа из 3 колонок: Кол-во, Цена, Сумма
    return shownDetails.map((detail: any) => ({
      title: `${detail.forName}`,
      children: [
        {
          title: 'Кол-во',
          dataIndex: ['details', detail.idx, 'quantity'],
          key: `qty-${detail.idx}`,
          width: 90,
          render: (_: any, record: any) => {
            const d = record.details[detail.idx];
            return d ? (d.dayQuantity || 0) + (d.nightQuantity || 0) - (d.wasteQuantity || 0) : 0;
          },
        },
        {
          title: 'Цена',
          dataIndex: ['details', detail.idx, 'price'],
          key: `price-${detail.idx}`,
          width: 90,
          render: (_: any, record: any) => {
            const d = record.details[detail.idx];
            return d ? d.price || (reportWorker?.pricings?.[detail.idx]?.perUnit || 0) : 0;
          },
        },
        {
          title: 'Сумма',
          dataIndex: ['details', detail.idx, 'sum'],
          key: `sum-${detail.idx}`,
          width: 90,
          render: (_: any, record: any) => {
            const d = record.details[detail.idx];
            const price = d ? d.price || (reportWorker?.pricings?.[detail.idx]?.perUnit || 0) : 0;
            const qty = d ? (d.dayQuantity || 0) + (d.nightQuantity || 0) - (d.wasteQuantity || 0) : 0;
            return (qty * price).toLocaleString(undefined, { maximumFractionDigits: 2 });
          },
        },
      ],
    }));
  }, [salaryReports, reportWorker]);

  const reportColumns = React.useMemo(() => [
    { 
      title: '№', 
      key: 'index', 
      fixed: 'left' as 'left', 
      width: 60,
      render: (_: any, __: any, index: number) => index + 1
    },
    { title: 'Дата', dataIndex: 'date', key: 'date', fixed: 'left' as 'left', width: 100 },
    ...detailColumnsGrouped,
    {
      title: 'ЮК',
      children: [
        { title: 'кол', dataIndex: 'yuk', key: 'yuk', width: 80 },
        { title: 'сумма', key: 'yukSum', width: 120, render: (_: any, record: any) => ((record.yuk || 0) * yukPrice).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' ' + salaryCurrency },
      ],
    },
    {
      title: 'Сумма за день',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 120,
      render: (value: any) => value ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' ' + salaryCurrency : '0 ' + salaryCurrency,
    },
  ], [detailColumnsGrouped, yukPrice, salaryCurrency]);

  const handleExport = () => {
    const exportParams: Record<string, any> = {};
    if (searchTerm) {
      exportParams.search = searchTerm;
    }
    if (hasDebt !== undefined) {
      exportParams.hasDebt = hasDebt ? "true" : "false";
    }
    if (currency) {
      exportParams.currency = currency;
    }
    if (salaryType) {
      exportParams.salaryType = salaryType;
    }
    downloadFile("/salary/export/excel", "salaries_export.xlsx", exportParams);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleEditPerDay = async (values: { perDay: number }) => {
    setConfirmLoading(true);

    try {
      await api.put(`/salary/per-day/${selectedSalary?.workerId}`, values);
      message.success("Цены успешно обновлены.");
      fetchSalaries();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating report", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setIsPerDayOpen(false);
    }
  };

  const handleSubmitForm = (values: any) => {
    setConfirmLoading(true);

    const updatedPricings = selectedSalary?.pricings.map((pricing) => {
      const formData = values[pricing._id];

      const updatedPricing = {
        forName: formData.forName || pricing.forName,
        ...(selectedSalary?.salaryType === SalaryType.PERMONTH
          ? { perMonth: formData.perMonth }
          : { perUnit: formData.perUnit }),
      };

      return updatedPricing;
    });

    api
      .put(`/salary/${selectedSalary?.workerId}`, { pricings: updatedPricings })
      .then(() => {
        message.success("Цены успешно обновлены.");
        setIsModalOpen(false);
        fetchSalaries();
        setConfirmLoading(false);
      })
      .catch((error) => {
        console.error("Error updating prices:", error);
        message.error(error.response.data.error);
        setConfirmLoading(false);
      });
  };

  const handleTransactionCreate = async (values: any) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
        forcePayment,
      };

      await api.post(`/salary/${selectedSalary?._id}/transaction`, data);

      message.success("Оплата успешно создана.");

      setIsTransactionOpen(false);
      setForcePayment(false);
      setConfirmLoading(false);
      fetchSalaries();
      transactionForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving cashbox:", error);

      // Долг мог обнулиться в другой вкладке — переключаемся на удаление сотрудника
      if (error.response?.data?.code === "NO_SALARY_DEBT") {
        setIsTransactionOpen(false);
        transactionForm.resetFields();
        setIsDeleteWorkerOpen(true);
        fetchSalaries();
        return;
      }

      message.error(error.response.data.error);
    }
  };

  const handleWorkerDeleted = () => {
    setSelectedSalary(null);
    fetchSalaries();
  };

  const handleAddPricing = () => {
    const newPricing: ISalary["pricings"][0] = {
      _id: Date.now().toString(),
      forName: "",
      perUnit:
        selectedSalary?.salaryType === SalaryType.PERUNIT ? 0 : undefined,
      perMonth:
        selectedSalary?.salaryType === SalaryType.PERMONTH ? 0 : undefined,
    };
    const updatedPricings = selectedSalary?.pricings.concat(newPricing);

    setSelectedSalary({
      ...selectedSalary!,
      pricings: updatedPricings!,
    });

    form.setFieldsValue({
      [newPricing._id]: {
        forName: newPricing.forName,
        perUnit: newPricing.perUnit,
        perMonth: newPricing.perMonth,
      },
    });
  };

  const handleModalOpen = (salary: ISalary) => {
    if (salary.salaryType === SalaryType.PERDAY) {
      setIsPerDayOpen(true);
      setSelectedSalary(salary);
      perDayForm.setFieldsValue({ perDay: salary.perDay });
    } else {
      setIsModalOpen(true);
      setSelectedSalary(salary);

      const initialValues = salary.pricings.reduce(
        (acc: { [key: string]: any }, pricing) => {
          acc[pricing._id] = {
            forName: pricing.forName,
            perMonth: pricing.perMonth,
            perUnit: pricing.perUnit,
          };
          return acc;
        },
        {}
      );

      form.setFieldsValue(initialValues);
    }
  };

  const handleTransactionClick = (salary: ISalary) => {
    setSelectedSalary(salary);
    setForcePayment(false);

    // Долга нет — предлагаем удалить сотрудника либо оплатить принудительно
    if (salary.debt <= 0) {
      setIsDeleteWorkerOpen(true);
      return;
    }

    setIsTransactionOpen(true);
  };

  // Оплата вопреки отсутствию долга — из модалки удаления
  const handlePayAnyway = () => {
    setForcePayment(true);
    setIsTransactionOpen(true);
  };

  const handleEditDebtPaidClick = (salary: ISalary) => {
    setIsEditDebtPaidOpen(true);
    setSelectedSalary(salary);
    editDebtPaidForm.setFieldsValue({
      debt: salary.debt,
      paid: salary.paid,
    });
  };

  const handleSalaryHistoryClick = (salary: ISalary) => {
    setIsSalaryHistoryOpen(true);
    setSelectedSalary(salary);
  };

  const handleBonusClick = (salary: ISalary) => {
    setIsBonusOpen(true);
    setSelectedSalary(salary);
  };

  const handleBonusCreate = async (values: any) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
      };

      await api.post(`/salary/bonus/${selectedSalary?.workerId}`, data);

      message.success("Премия успешно создана.");

      setIsBonusOpen(false);
      setConfirmLoading(false);
      fetchSalaries();
      bonusForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating bonus:", error);
      message.error(error.response.data.error);
    }
  };

  const handleEditDebtPaid = async (values: { debt: number; paid: number }) => {
    setConfirmLoading(true);
    try {
      await api.put(`/salary/${selectedSalary?.workerId}/debt-paid`, values);
      message.success("Долг и оплата успешно обновлены.");
      fetchSalaries();
      setIsEditDebtPaidOpen(false);
    } catch (error: any) {
      console.error("Error updating debt and paid:", error);
      message.error(error.response?.data?.error || "Ошибка при обновлении");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDeletePricing = (pricingId: string) => {
    const updatedPricings = selectedSalary?.pricings.filter(
      (pricing) => pricing._id !== pricingId
    );

    setSelectedSalary({
      ...selectedSalary!,
      pricings: updatedPricings!,
    });
  };

  const handleOpenReportModal = async (salary: ISalary) => {
    setReportWorker(salary);
    setIsReportModalOpen(true);
    setReportDateRange(["", ""]);
    setSalaryReports([]);
    setSalaryTransactions([]);
    // Получаем yukPrice при открытии модалки
    try {
      const res = await api.get("/settings");
      setYukPriceUSD(res.data.settings?.yukPriceUSD || 0);
      setYukPriceUZS(res.data.settings?.yukPriceUZS || 0);
    } catch (e) {
      setYukPriceUSD(0);
      setYukPriceUZS(0);
    }
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
    setReportWorker(null);
    setSalaryReports([]);
    setSalaryTransactions([]);
  };

  const fetchSalaries = async () => {
    setLoading(true);
    try {
      const response = await api.get("/salary", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
          hasDebt: hasDebt ? true : undefined,
          currency,
          salaryType,
        },
      });

      setSalaries(response.data.salaries);
      setTotal(response.data.total);
      setOurDebtUSD(response.data.totalCompanyDebtUSD || 0);
      setEmployeesDebtUSD(response.data.totalEmployeeDebtUSD || 0);
    } catch (error) {
      console.error("Error fetching salaries", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalaries();
  }, [page, searchTerm, pageSize, hasDebt, currency, salaryType]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  useEffect(() => {
    if (exchangeRate) {
      bonusForm.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, bonusForm]);

  const columns = [
    {
      title: "Имя",
      dataIndex: "workerName",
      key: "workerName",
      fixed: "left" as "left",
    },
    {
      title: "Фамилия",
      dataIndex: "workerSurname",
      key: "workerSurname",
    },
    {
      title: "Тип зарплаты",
      dataIndex: "salaryType",
      key: "salaryType",
    },
    {
      title: "Валюта зарплаты",
      dataIndex: "salaryCurrency",
      key: "salaryCurrency",
    },
    {
      title: "Заплатили",
      dataIndex: "paid",
      key: "paid",
    },
    {
      title: "Надо заплатить",
      dataIndex: "debt",
      key: "debt",
    },
    {
      title: "Цены",
      key: "pricings",
      render: (_: any, record: ISalary) => {
        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              maxWidth: "1400px",
            }}
          >
            {record.pricings.map((pricing, index) => (
              <p key={index}>
                {index + 1}. {pricing.forName}, Ставка:{pricing.perUnit}
                {pricing.perMonth}
                {pricing.quantity
                  ? `, Кол-во выполнено: ${pricing.quantity}`
                  : null}
              </p>
            ))}
          </div>
        );
      },
    },
    {
      title: "За день",
      dataIndex: "perDay",
      key: "perDay",
    },
    {
      title: "Начало",
      dataIndex: "periodStart",
      key: "periodStart",
    },
    {
      title: "Обновлено",
      dataIndex: "lastUpdate",
      key: "lastUpdate",
    },
    {
      title: "",
      key: "actions",
      fixed: "right" as "right",
      render: (_: any, record: ISalary) => {
        const menu = (
          <Menu>
            {/* Действия редактирования */}
            {(canEdit(authState.user?.role) ||
              authState.user?.role === UserRole.Accountant) && (
                <Menu.Item onClick={() => handleModalOpen(record)}>
                  Изменить цену
                </Menu.Item>
              )}
              
            {canEdit(authState.user?.role) && (
              <Menu.Item onClick={() => handleEditDebtPaidClick(record)}>
                Изменить долг и оплату
              </Menu.Item>
            )}
            
            {(canEdit(authState.user?.role) ||
              authState.user?.role === UserRole.Cashier ||
              authState.user?.role === UserRole.Accountant) && (
                <>
                  <Menu.Item onClick={() => handleTransactionClick(record)}>
                    Добавить оплату
                  </Menu.Item>
                  <Menu.Item onClick={() => handleBonusClick(record)}>
                    Добавить премию
                  </Menu.Item>
                </>
              )}
              
            {/* Действия просмотра (GET запросы) */}
            <Menu.Item onClick={() => handleOpenReportModal(record)}>
              Отчет
            </Menu.Item>
            
            {(authState.user?.role === UserRole.Admin ||
              authState.user?.role === UserRole.Cashier ||
              authState.user?.role === UserRole.Accountant ||
              authState.user?.role === "director") && (
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/${record.workerId}/salary/${record.workerName}/USD&UZS/transactions`
                    )
                  }
                >
                  Смотреть транзакции
                </Menu.Item>
              )}
            <Menu.Item onClick={() => handleSalaryHistoryClick(record)}>
              <Space>
                <HistoryOutlined />
                История зарплат
              </Space>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  // --- Функция конвертации суммы транзакции в валюту отчёта ---
  function convertToReportCurrency(amount: number, txCurrency: string, reportCurrency: string, rate: number) {
    if (txCurrency === reportCurrency) return amount;
    if (txCurrency === 'USD' && reportCurrency === 'UZS') return amount * rate;
    if (txCurrency === 'UZS' && reportCurrency === 'USD') return amount / rate;
    return amount; // fallback
  }

  // --- Фильтры отчёта зарплаты: одни и те же для печати и для Excel ---
  const buildSalaryReportParams = () => {
    const params: Record<string, any> = {};
    if (searchTerm) params.search = searchTerm;
    if (hasDebt !== undefined) params.hasDebt = hasDebt ? "true" : "false";
    if (currency) params.currency = currency;
    if (salaryType) params.salaryType = salaryType;
    return params;
  };

  // --- Тот же отчёт зарплаты, но книгой Excel ---
  const handleDownloadSalaryReportExcel = () => {
    const fileName = `salary_report_${dayjs().format("YYYY-MM-DD")}.xlsx`;
    downloadFile("/salary/print-report/excel", fileName, buildSalaryReportParams());
  };

  // --- Функция получения отчета зарплаты для печати ---
  const handlePrintSalaryReport = async () => {
    setLoading(true);
    try {
      const params = buildSalaryReportParams();

      const response = await api.get("/salary/print-report", { params });
      const data = response.data;

      // Итог по авансу считает бэкенд — тем же кодом, что и для Excel-варианта
      const totalAdvanceUSD = data.totals.totalAdvanceUSD || 0;
      const totalAdvanceUZS = data.totals.totalAdvanceUZS || 0;

      // Формируем HTML таблицу для печати
      const tableRows = data.salaries.map((salary: any, index: number) => {
        const perMonthTotal = (salary.pricings || []).reduce((sum: number, pricing: any) => sum + (pricing.perMonth || 0), 0);
        const debt = salary.debt || 0;
        const isPerMonth = salary.salaryType === SalaryType.PERMONTH;
        const advance = isPerMonth ? perMonthTotal - debt : 0; // Аванс только для PERMONTH
        const advanceDisplay = isPerMonth 
          ? advance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : '-';
        
        return `
          <tr>
            <td style="text-align: center;">${index + 1}</td>
            <td>${salary.workerName || ''}</td>
            <td>${salary.workerSurname || ''}</td>
            <td style="text-align: center;">${salary.salaryCurrency || ''}</td>
            <td style="text-align: right;">${perMonthTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
            <td style="text-align: right;">${advanceDisplay}</td>
            <td style="text-align: right;">${debt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
        `;
      }).join('');

      const printContent = `
        <html>
          <head>
            <title>Отчет зарплаты</title>
            <style>
              @page {
                size: A4;
                margin: 15mm;
              }
              body {
                font-family: Arial, sans-serif;
                font-size: 11px;
              }
              h2 {
                text-align: center;
                margin-bottom: 20px;
                font-size: 16px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 11px;
                margin-bottom: 20px;
              }
              th, td {
                border: 1px solid #000;
                padding: 6px 8px;
              }
              th {
                background-color: #f2f2f2;
                font-weight: bold;
                text-align: center;
              }
              .totals {
                margin-top: 20px;
                font-size: 12px;
              }
              .totals-section {
                margin-bottom: 15px;
                padding: 10px;
                background-color: #f9f9f9;
                border: 1px solid #ddd;
              }
              .totals-title {
                font-weight: bold;
                margin-bottom: 5px;
                font-size: 13px;
              }
              .totals-row {
                margin: 3px 0;
                padding-left: 10px;
              }
            </style>
          </head>
          <body>
            <h2>Отчет зарплаты</h2>
            <table>
              <thead>
                <tr>
                  <th style="width: 40px;">№</th>
                  <th>Имя</th>
                  <th>Фамилия</th>
                  <th style="width: 80px;">Валюта</th>
                  <th style="width: 120px;">За месяц</th>
                  <th style="width: 120px;">Аванс</th>
                  <th style="width: 120px;">Надо заплатить</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
            
            <div class="totals">
              <div class="totals-section">
                <div class="totals-title">За месяц:</div>
                <div class="totals-row">USD: ${data.totals.totalPerMonthUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div class="totals-row">UZS: ${data.totals.totalPerMonthUZS.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              
              <div class="totals-section">
                <div class="totals-title">Аванс (только PERMONTH):</div>
                <div class="totals-row">USD: ${totalAdvanceUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div class="totals-row">UZS: ${totalAdvanceUZS.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              
              <div class="totals-section">
                <div class="totals-title">Надо заплатить:</div>
                <div class="totals-row">USD: ${data.totals.totalDebtUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div class="totals-row">UZS: ${data.totals.totalDebtUZS.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        message.error('Не удалось открыть окно печати');
        return;
      }

      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } catch (error: any) {
      console.error('Ошибка при получении отчета:', error);
      message.error(error.response?.data?.error || 'Ошибка при получении отчета');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTotalsModal = () => {
    setIsTotalsModalOpen(true);
    setTotalsDateRange(["", ""]);
  };

  const handleDownloadTotals = async () => {
    if (!totalsDateRange?.[0] || !totalsDateRange?.[1]) {
      message.warning("Выберите период");
      return;
    }
    setTotalsLoading(true);
    try {
      const startDate = toDDMMYY(totalsDateRange[0]);
      const endDate = toDDMMYY(totalsDateRange[1]);
      const fileName = `salary_totals_${totalsDateRange[0]}_${totalsDateRange[1]}.xlsx`;
      await downloadFile("/salary/totals/excel", fileName, { startDate, endDate });
      setIsTotalsModalOpen(false);
    } finally {
      setTotalsLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Зарплата</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Select
          allowClear
          placeholder="Фильтр по валюте"
          onChange={(value) => setCurrency(value)}
          style={{ width: 150 }}
        >
          {Object.values(Currency).map((currency) => (
            <Select.Option key={currency} value={currency}>
              {currency}
            </Select.Option>
          ))}
        </Select>

        <Select
          allowClear
          placeholder="Фильтр по типу"
          onChange={(value) => setSalaryType(value)}
          style={{ width: 150 }}
        >
          {Object.values(SalaryType).map((type) => (
            <Select.Option key={type} value={type}>
              {type}
            </Select.Option>
          ))}
        </Select>
        <Checkbox
          checked={hasDebt}
          onChange={(e) => setHasDebt(e.target.checked)}
        >
          Есть долг
        </Checkbox>
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Button
          type="primary"
          onClick={async () => {
            setIsAllWorkersReportModalOpen(true);
            setAllWorkersReportDateRange(["", ""]);
            setAllWorkersData([]);
            // Получаем yukPrice при открытии модалки
            try {
              const res = await api.get("/settings");
              setYukPriceUSD(res.data.settings?.yukPriceUSD || 0);
              setYukPriceUZS(res.data.settings?.yukPriceUZS || 0);
            } catch (e) {
              setYukPriceUSD(0);
              setYukPriceUZS(0);
            }
          }}
        >
          Отчет всех сотрудников
        </Button>
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="excel" icon={<PiMicrosoftExcelLogoFill />} onClick={handleDownloadSalaryReportExcel}>
                Скачать в Excel
              </Menu.Item>
              <Menu.Item key="print" onClick={handlePrintSalaryReport}>
                Печать (PDF)
              </Menu.Item>
            </Menu>
          }
        >
          <Button type="default" loading={downloadLoading}>
            Получить отчет зарплаты <DownOutlined />
          </Button>
        </Dropdown>
        <Button type="default" onClick={handleOpenTotalsModal} loading={totalsLoading || downloadLoading}>
          Получить итоги
        </Button>
        <div style={{ marginLeft: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Card size="small" style={{ borderColor: '#ff4d4f', background: '#fff1f0' }}>
            <Statistic
              title="Общ.наш долг"
              value={ourDebtUSD}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
              suffix="USD"
            />
          </Card>
          <Card size="small" style={{ borderColor: '#52c41a', background: '#f6ffed' }}>
            <Statistic
              title="Общ.долг сотрудников"
              value={employeesDebtUSD}
              precision={2}
              valueStyle={{ color: '#389e0d' }}
              suffix="USD"
            />
          </Card>
        </div>
      </Space>
      <Table
        loading={loading}
        columns={columns}
        dataSource={salaries}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title={`Получить итоги${totalsDateRange && totalsDateRange[0] && totalsDateRange[1] ? ' (' + formatPeriod(totalsDateRange) + ')' : ''}`}
        open={isTotalsModalOpen}
        onCancel={() => setIsTotalsModalOpen(false)}
        onOk={handleDownloadTotals}
        okText="Скачать Excel"
        confirmLoading={totalsLoading}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span>Период:</span>
          <RangePicker
            format="DD.MM.YY"
            value={
              totalsDateRange && totalsDateRange[0] && totalsDateRange[1]
                ? [
                    totalsDateRange[0] ? dayjs(totalsDateRange[0], "YYYY-MM-DD") : null,
                    totalsDateRange[1] ? dayjs(totalsDateRange[1], "YYYY-MM-DD") : null,
                  ]
                : null
            }
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setTotalsDateRange([dates[0].format("YYYY-MM-DD"), dates[1].format("YYYY-MM-DD")]);
              } else {
                setTotalsDateRange(["", ""]);
              }
            }}
            allowClear={false}
          />
        </div>
        <div style={{ marginTop: 12, color: "#888" }}>
          Excel будет сформирован за выбранный период и сгруппирован по деталям.
        </div>
      </Modal>

      <Modal
        title="Изменить цены"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          {selectedSalary?.pricings.map((pricing) => (
            <div key={pricing._id}>
              <Form.Item name={[pricing._id, "forName"]} label="Название">
                <Input />
              </Form.Item>

              {selectedSalary?.salaryType === SalaryType.PERMONTH ? (
                <Form.Item
                  name={[pricing._id, "perMonth"]}
                  label="Ставка за месяц"
                >
                  <InputNumber min={0} />
                </Form.Item>
              ) : (
                <Form.Item
                  name={[pricing._id, "perUnit"]}
                  label="Ставка за единицу"
                >
                  <InputNumber min={0} />
                </Form.Item>
              )}

              {pricing.for === undefined && (
                <Button onClick={() => handleDeletePricing(pricing._id)}>
                  Удалить
                </Button>
              )}
            </div>
          ))}

          <Button type="primary" onClick={handleAddPricing}>
            Добавить новый прайсинг
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Добавить оплату"
        open={isTransactionOpen}
        onCancel={() => {
          setIsTransactionOpen(false);
          setForcePayment(false);
        }}
        onOk={() => transactionForm.submit()}
        confirmLoading={confirmLoading}
      >
        {forcePayment && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="У сотрудника нет долга по зарплате"
            description="Оплата будет проведена сверх долга и уведёт его в минус."
          />
        )}
        <Form
          form={transactionForm}
          onFinish={handleTransactionCreate}
          layout="vertical"
        >
          <Form.Item
            name="cashboxId"
            label="Касса"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите кассу" loading={loading}>
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Select.Option key={cashbox._id} value={cashbox._id}>
                  Имя: {cashbox.name} | Валюта: {cashbox.currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="machineId"
            label="Станок"
            rules={[{ required: true, message: "Выберите станок" }]}
          >
            <Select
              placeholder="Выберите станок"
              allowClear
              loading={machineListLoading}
            >
              {machineList?.map((machine: Partial<Machine>) => (
                <Select.Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Сумма"
            rules={[{ required: true, message: "Введите сумму" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            hidden={true}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите описание." }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Добавить премию"
        open={isBonusOpen}
        onCancel={() => setIsBonusOpen(false)}
        onOk={() => bonusForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form
          form={bonusForm}
          onFinish={handleBonusCreate}
          layout="vertical"
        >
          <Form.Item
            name="cashboxId"
            label="Касса"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите кассу" loading={cashboxesListLoading}>
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Select.Option key={cashbox._id} value={cashbox._id}>
                  Имя: {cashbox.name} | Валюта: {cashbox.currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="machineId"
            label="Станок"
            rules={[{ required: true, message: "Выберите станок" }]}
          >
            <Select
              placeholder="Выберите станок"
              allowClear
              loading={machineListLoading}
            >
              {machineList?.map((machine: Partial<Machine>) => (
                <Select.Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Сумма премии"
            rules={[{ required: true, message: "Введите сумму премии" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            hidden={true}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание премии"
            rules={[{ required: true, message: "Введите описание премии." }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Цена в день"
        open={isPerDayOpen}
        onCancel={() => setIsPerDayOpen(false)}
        onOk={() => perDayForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={perDayForm} layout="vertical" onFinish={handleEditPerDay}>
          <Form.Item
            name="perDay"
            label="Цена в день"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите цену",
              },
            ]}
          >
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Изменить долг и оплату"
        open={isEditDebtPaidOpen}
        onCancel={() => setIsEditDebtPaidOpen(false)}
        onOk={() => editDebtPaidForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={editDebtPaidForm} layout="vertical" onFinish={handleEditDebtPaid}>
          <Form.Item
            name="debt"
            label="Долг"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите долг",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="paid"
            label="Оплачено"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите сумму оплаты",
              },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно истории зарплат работника */}
      <WorkerSalaryHistory
        workerId={selectedSalary?.workerId || ""}
        workerName={selectedSalary?.workerName || ""}
        workerSurname={selectedSalary?.workerSurname}
        visible={isSalaryHistoryOpen}
        onClose={() => setIsSalaryHistoryOpen(false)}
      />

      {/* Предложение удалить сотрудника, когда долг по зарплате отсутствует */}
      <DeleteWorkerModal
        workerId={selectedSalary?.workerId || ""}
        workerName={selectedSalary?.workerName || ""}
        workerSurname={selectedSalary?.workerSurname}
        open={isDeleteWorkerOpen}
        fromPayment
        onClose={() => setIsDeleteWorkerOpen(false)}
        onDeleted={handleWorkerDeleted}
        onPayAnyway={handlePayAnyway}
      />

      <Modal
        title={`Отчет: ${reportWorker?.workerName || ''}${reportDateRange && reportDateRange[0] && reportDateRange[1] ? ' (' + formatPeriod(reportDateRange) + ')' : ''}`}
        open={isReportModalOpen}
        onCancel={handleCloseReportModal}
        footer={null}
        width={1500}
      >
        <div style={{ marginBottom: 16 }}>
          <span>Период: </span>
          <RangePicker
            style={{ marginRight: 8 }}
            format="DD.MM.YY"
            value={reportDateRange && reportDateRange[0] && reportDateRange[1] ? [
              reportDateRange[0] ? dayjs(reportDateRange[0], "YYYY-MM-DD") : null,
              reportDateRange[1] ? dayjs(reportDateRange[1], "YYYY-MM-DD") : null
            ] : null}
            onChange={(dates, dateStrings) => {
              if (dates && dateStrings) {
                setReportDateRange([
                  dates[0]?.format("YYYY-MM-DD") || '',
                  dates[1]?.format("YYYY-MM-DD") || ''
                ]);
              } else {
                setReportDateRange(["", ""]);
              }
            }}
            allowClear={false}
          />
          <Button
            type="primary"
            loading={reportLoading}
            onClick={async () => {
              if (!reportWorker || !reportDateRange?.[0] || !reportDateRange?.[1]) return;
              setReportLoading(true);
              try {
                const startDate = toDDMMYY(reportDateRange[0]);
                const endDate = toDDMMYY(reportDateRange[1]);
                const res = await api.get(`/salary/full-report/${reportWorker.workerId}`, {
                  params: {
                    startDate: startDate,
                    endDate: endDate,
                  },
                });
                setSalaryReports(res.data.reports);
                setSalaryTransactions(res.data.transactions);
              } catch (e) {
                message.error('Ошибка загрузки отчета');
              } finally {
                setReportLoading(false);
              }
            }}
          >
            Показать
          </Button>
          <Button
            style={{ marginLeft: 8 }}
            onClick={() => {
              // Печать: открыть новое окно с разметкой для печати
              const printWindow = window.open('', '_blank');
              if (!printWindow) return;
              const tableHtml = document.getElementById('salary-report-table')?.outerHTML || '';
              const txHtml = document.getElementById('salary-tx-table')?.outerHTML || '';
              const summaryHtml = document.getElementById('salary-print-summary')?.outerHTML || '';
              
              // Расчет итогов для печати
              let totalEarned = 0;
              let printSummaryHtml = '';
              
              if (reportWorker?.salaryType === SalaryType.PERMONTH) {
                // Для зарплаты за месяц
                totalEarned = reportWorker?.pricings?.reduce((acc, pricing) => acc + (pricing.perMonth || 0), 0) || 0;
              } else {
                // Для других типов зарплаты
                totalEarned = salaryReports.reduce((acc, report) => {
                  // Пробуем разные варианты названий поля с суммой
                  const reportSum = report.totalPrice || report.total || report.sum || report.amount || 0;
                  return acc + reportSum;
                }, 0);
              }
              
              // Разделяем транзакции на оплаты и премии
              const payments = salaryTransactions.filter(tx => tx.expenseType !== ExpenseType.BONUS);
              const bonuses = salaryTransactions.filter(tx => tx.expenseType === ExpenseType.BONUS);
              
              const totalPayments = payments.reduce((acc, tx) => {
                const txCur = tx.currency || tx.salaryCurrency || 'USD';
                return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
              }, 0);
              
              const totalBonuses = bonuses.reduce((acc, tx) => {
                const txCur = tx.currency || tx.salaryCurrency || 'USD';
                return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
              }, 0);
              
              const totalToPay = totalEarned - totalPayments; // Только оплаты вычитаем из зарплаты
              
              if (reportWorker?.salaryType === SalaryType.PERMONTH) {
                printSummaryHtml = `
                  <div style="margin-top: 16px; font-size: 10px;">
                    <div>Зарплата за месяц: <b>${totalEarned.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                    <div>Оплаты: <b>${totalPayments.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                    <div>Премии: <b>${totalBonuses.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                  </div>
                `;
              } else {
                printSummaryHtml = `
                  <div style="margin-top: 16px; font-size: 10px;">
                    <div>Общ заработано: <b>${totalEarned.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                    <div>Оплаты: <b>${totalPayments.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                    <div>Премии: <b>${totalBonuses.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                    <div>Надо заплатить: <b>${totalToPay.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                  </div>
                `;
              }
              
              printWindow.document.write(`
                <html>
                  <head>
                    <title>Печать отчета</title>
                    <style>
                      body, table, th, td { font-family: Arial, sans-serif; font-size: 10px !important; padding: 0 !important; margin: 0 !important; border: 1px solid #000 !important; }
                      th { background: #f5f5f5; }
                      h3 { margin-top: 16px; font-size: 11px; }
                      .no-print { display: none !important; }
                      #salary-print-summary { display: block !important; }
                    </style>
                  </head>
                  <body>
                    <h2 style="font-size:12px; margin:0 0 2px 0;">Отчет: ${reportWorker?.workerName || ''}</h2>
                    <div style="font-size:10px; margin-bottom:2px;">${formatPeriod(reportDateRange)}</div>
                    ${tableHtml}
                    ${txHtml}
                    ${printSummaryHtml}
                  </body>
                </html>
              `);
              printWindow.document.close();
              printWindow.focus();
              printWindow.print();
            }}
          >
            Печать
          </Button>
        </div>
        {reportLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>Загрузка...</div>
        ) : salaryReports.length === 0 && salaryTransactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>Нет данных за выбранный период</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {/* Если есть отчеты, показываем детальный отчет для всех типов зарплаты */}
            {salaryReports.length > 0 ? (
              <div>
                {/* --- АНТД ТАБЛИЦА ОТЧЕТА --- */}
                <Table
                  id="salary-report-table"
                  dataSource={salaryReports.map((report, idx) => ({ ...report, key: report._id || idx }))}
                  pagination={false}
                  bordered
                  scroll={{ x: true }}
                  summary={pageData => {
                    const totalYuk = pageData.reduce((acc, report) => acc + (report.yuk || 0), 0);
                    const totalYukSum = pageData.reduce((acc, report) => acc + ((report.yuk || 0) * yukPrice), 0);
                    // Итог по транзакциям с учётом конвертации
                    const totalTx = salaryTransactions.reduce((acc, tx) => {
                      const txCur = tx.currency || tx.salaryCurrency || 'USD';
                      return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                    }, 0);
                    // Итоги по деталям
                    const detailTotals = getDetailTotals([...pageData], detailColumnsGrouped, reportWorker);
                    return (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}></Table.Summary.Cell>
                          <Table.Summary.Cell index={1}><b>Итого</b></Table.Summary.Cell>
                          {detailColumnsGrouped.map((col: any, idx: number) => [
                            <Table.Summary.Cell key={`q-${idx}`} index={2 + idx * 3} />,
                            <Table.Summary.Cell key={`p-${idx}`} index={3 + idx * 3} />,
                            <Table.Summary.Cell key={`s-${idx}`} index={4 + idx * 3} />
                          ])}
                          <Table.Summary.Cell index={100}><b>{totalYuk}</b></Table.Summary.Cell>
                          <Table.Summary.Cell index={101}><b>{totalYukSum.toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></Table.Summary.Cell>
                        </Table.Summary.Row>
                        {/* Итоги по деталям (только на экране, не в печати) */}
                        <Table.Summary.Row className="ant-table-summary-detail-totals no-print">
                          <Table.Summary.Cell index={0}></Table.Summary.Cell>
                          <Table.Summary.Cell index={1}><span style={{ fontWeight: 400 }}>Сумма по деталям</span></Table.Summary.Cell>
                          {detailTotals.map((tot, idx) => [
                            <Table.Summary.Cell key={`q-tot-${idx}`} index={2 + idx * 3}><b>{tot.qty}</b></Table.Summary.Cell>,
                            <Table.Summary.Cell key={`p-tot-${idx}`} index={3 + idx * 3}></Table.Summary.Cell>,
                            <Table.Summary.Cell key={`s-tot-${idx}`} index={4 + idx * 3}><b>{tot.sum.toLocaleString(undefined, { maximumFractionDigits: 2 })}</b></Table.Summary.Cell>
                          ])}
                          <Table.Summary.Cell index={100}></Table.Summary.Cell>
                          <Table.Summary.Cell index={101}></Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    );
                  }}
                  columns={reportColumns}
                />
                {/* --- АНТД ТАБЛИЦА ТРАНЗАКЦИЙ --- */}
                {salaryTransactions.length > 0 && (
                  <div style={{ marginTop: 32 }}>
                    <h3>Транзакции</h3>
                    <Table
                      id="salary-tx-table"
                      dataSource={salaryTransactions.map((tx, idx) => ({ ...tx, key: tx._id || idx }))}
                      pagination={false}
                      bordered
                      columns={[
                        { 
                          title: '№', 
                          key: 'index', 
                          width: 60,
                          render: (_: any, __: any, index: number) => index + 1
                        },
                        { title: 'Дата', dataIndex: 'date', key: 'date', render: (v, r) => v || (r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '') },
                        {
                          title: 'Тип',
                          dataIndex: 'expenseType',
                          key: 'expenseType',
                          render: (v) => v === ExpenseType.BONUS ? 'Премия' : 'Оплата'
                        },
                        {
                          title: 'Сумма',
                          dataIndex: 'amount',
                          key: 'amount',
                          render: (v, r) => {
                            const txCur = r.currency || r.salaryCurrency || 'USD';
                            if (txCur === salaryCurrency) return v;
                            // Показываем и оригинал, и эквивалент
                            const converted = convertToReportCurrency(v, txCur, salaryCurrency, r.exchangeRate || exchangeRate || 1);
                            return `${v} ${txCur} (${converted.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency})`;
                          }
                        },
                        { title: 'Валюта', dataIndex: 'currency', key: 'currency', render: (v, r) => v || r.salaryCurrency || salaryCurrency },
                        { title: 'Описание', dataIndex: 'description', key: 'description' },
                      ]}
                    />
                  </div>
                )}
                {/* Итоги */}
                <div style={{ marginTop: 8 }}>
                  {reportWorker?.salaryType === SalaryType.PERMONTH ? (
                    <>
                      <div>Зарплата за месяц: <b>{(reportWorker?.pricings?.reduce((acc, pricing) => acc + (pricing.perMonth || 0), 0) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Оплаты: <b>{salaryTransactions.filter(tx => tx.expenseType !== ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Премии: <b>{salaryTransactions.filter(tx => tx.expenseType === ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                    </>
                  ) : (
                    <>
                      <div>Общ заработано: <b>{salaryReports.reduce((acc, report) => {
                        // Пробуем разные варианты названий поля с суммой
                        const reportSum = report.totalPrice || report.total || report.sum || report.amount || 0;
                        return acc + reportSum;
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Оплаты: <b>{salaryTransactions.filter(tx => tx.expenseType !== ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Премии: <b>{salaryTransactions.filter(tx => tx.expenseType === ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Надо заплатить: <b>{(
                        salaryReports.reduce((acc, report) => {
                          // Пробуем разные варианты названий поля с суммой
                          const reportSum = report.totalPrice || report.total || report.sum || report.amount || 0;
                          return acc + reportSum;
                        }, 0) -
                        salaryTransactions.filter(tx => tx.expenseType !== ExpenseType.BONUS).reduce((acc, tx) => {
                          const txCur = tx.currency || tx.salaryCurrency || 'USD';
                          return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                        }, 0)
                      ).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* Если отчетов нет, но есть транзакции, показываем только транзакции */
              salaryTransactions.length > 0 ? (
                <div>
                  <h3>Транзакции</h3>
                  <Table
                    id="salary-tx-table"
                    dataSource={salaryTransactions.map((tx, idx) => ({ ...tx, key: tx._id || idx }))}
                    pagination={false}
                    bordered
                    columns={[
                      { 
                        title: '№', 
                        key: 'index', 
                        width: 60,
                        render: (_: any, __: any, index: number) => index + 1
                      },
                      { title: 'Дата', dataIndex: 'date', key: 'date', render: (v, r) => v || (r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '') },
                      {
                        title: 'Тип',
                        dataIndex: 'expenseType',
                        key: 'expenseType',
                        render: (v) => v === ExpenseType.BONUS ? 'Премия' : 'Оплата'
                      },
                      {
                        title: 'Сумма',
                        dataIndex: 'amount',
                        key: 'amount',
                        render: (v, r) => {
                          const txCur = r.currency || r.salaryCurrency || 'USD';
                          if (txCur === salaryCurrency) return v;
                          // Показываем и оригинал, и эквивалент
                          const converted = convertToReportCurrency(v, txCur, salaryCurrency, r.exchangeRate || exchangeRate || 1);
                          return `${v} ${txCur} (${converted.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency})`;
                        }
                      },
                      { title: 'Валюта', dataIndex: 'currency', key: 'currency', render: (v, r) => v || r.salaryCurrency || salaryCurrency },
                      { title: 'Описание', dataIndex: 'description', key: 'description' },
                    ]}
                  />
                  {/* Итоги для случаев без отчетов */}
                  {reportWorker?.salaryType === SalaryType.PERMONTH && (
                    <div style={{ marginTop: 8 }}>
                      <div>Зарплата за месяц: <b>{(reportWorker?.pricings?.reduce((acc, pricing) => acc + (pricing.perMonth || 0), 0) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Оплаты: <b>{salaryTransactions.filter(tx => tx.expenseType !== ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Премии: <b>{salaryTransactions.filter(tx => tx.expenseType === ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>Нет данных за выбранный период</div>
              )
            )}
          </div>
        )}
      </Modal>

      {/* Модальное окно отчета всех сотрудников */}
      <Modal
        title={`Отчет всех сотрудников${allWorkersReportDateRange && allWorkersReportDateRange[0] && allWorkersReportDateRange[1] ? ' (' + formatPeriod(allWorkersReportDateRange) + ')' : ''}`}
        open={isAllWorkersReportModalOpen}
        onCancel={() => {
          setIsAllWorkersReportModalOpen(false);
          setAllWorkersData([]);
        }}
        footer={null}
        width={1600}
      >
        <div style={{ marginBottom: 16 }}>
          <span>Период: </span>
          <RangePicker
            style={{ marginRight: 8 }}
            format="DD.MM.YY"
            value={allWorkersReportDateRange && allWorkersReportDateRange[0] && allWorkersReportDateRange[1] ? [
              allWorkersReportDateRange[0] ? dayjs(allWorkersReportDateRange[0], "YYYY-MM-DD") : null,
              allWorkersReportDateRange[1] ? dayjs(allWorkersReportDateRange[1], "YYYY-MM-DD") : null
            ] : null}
            onChange={(dates, dateStrings) => {
              if (dates && dateStrings) {
                setAllWorkersReportDateRange([
                  dates[0]?.format("YYYY-MM-DD") || '',
                  dates[1]?.format("YYYY-MM-DD") || ''
                ]);
              } else {
                setAllWorkersReportDateRange(["", ""]);
              }
            }}
            allowClear={false}
          />
          <Button
            type="primary"
            loading={allWorkersReportLoading}
            onClick={async () => {
              if (!allWorkersReportDateRange?.[0] || !allWorkersReportDateRange?.[1]) {
                message.warning('Выберите период');
                return;
              }
              setAllWorkersReportLoading(true);
              try {
                const startDate = toDDMMYY(allWorkersReportDateRange[0]);
                const endDate = toDDMMYY(allWorkersReportDateRange[1]);
                const res = await api.get(`/salary/full-report-all`, {
                  params: {
                    startDate: startDate,
                    endDate: endDate,
                  },
                });
                setAllWorkersData(res.data.workers || []);
              } catch (e: any) {
                message.error(e.response?.data?.error || 'Ошибка загрузки отчета');
              } finally {
                setAllWorkersReportLoading(false);
              }
            }}
          >
            Показать
          </Button>
        </div>
        {allWorkersReportLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>Загрузка...</div>
        ) : allWorkersData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>Нет данных за выбранный период</div>
        ) : (
          <>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Search
                placeholder="Поиск по имени сотрудника"
                onSearch={setAllWorkersSearchTerm}
                onChange={(e) => setAllWorkersSearchTerm(e.target.value)}
                allowClear
                style={{ flex: 1, maxWidth: 300 }}
              />
              <Button
                type="default"
                icon={<PiMicrosoftExcelLogoFill />}
                onClick={() => {
                  const filteredData = allWorkersSearchTerm
                    ? allWorkersData.filter((w) =>
                        w.workerName?.toLowerCase().includes(allWorkersSearchTerm.toLowerCase())
                      )
                    : allWorkersData;

                  // Создаем Excel файл
                  const workbook = XLSX.utils.book_new();

                  const usedSheetNames = new Set<string>();
                  
                  const getUniqueSheetName = (baseName: string): string => {
                    let uniqueName = baseName.substring(0, 31);
                    let counter = 1;
                    while (usedSheetNames.has(uniqueName)) {
                      const suffix = `_${counter}`;
                      uniqueName = (baseName.substring(0, 31 - suffix.length) + suffix).substring(0, 31);
                      counter++;
                    }
                    usedSheetNames.add(uniqueName);
                    return uniqueName;
                  };

                  filteredData.forEach((workerData, workerIdx) => {
                    const workerSalary = workerData.salary;
                    const workerReports = workerData.reports || [];
                    const workerTransactions = workerData.transactions || [];
                    const workerCurrency = workerSalary?.salaryCurrency || 'USD';
                    const workerYukPrice = workerCurrency === 'USD' ? yukPriceUSD : yukPriceUZS;

                    // Сначала определяем, какие детали имеют данные (хотя бы в одном отчете quantity > 0)
                    const detailIndicesWithData = new Set<number>();
                    if (workerReports.length > 0 && workerReports[0].details && Array.isArray(workerReports[0].details)) {
                      workerReports.forEach((report: any) => {
                        if (report.details && Array.isArray(report.details)) {
                          report.details.forEach((detail: any, detailIdx: number) => {
                            const qty = (detail.dayQuantity || 0) + (detail.nightQuantity || 0) - (detail.wasteQuantity || 0);
                            if (qty > 0) {
                              detailIndicesWithData.add(detailIdx);
                            }
                          });
                        }
                      });
                    }

                    // Подготовка данных для отчета
                    const reportData: any[] = [];
                    workerReports.forEach((report: any, idx: number) => {
                      const row: any = {
                        '№': idx + 1,
                        'Дата': report.date || '',
                        'ЮК (кол)': report.yuk || 0,
                        'ЮК (сумма)': ((report.yuk || 0) * workerYukPrice).toFixed(2),
                        'Сумма за день': (report.totalPrice || 0).toFixed(2),
                      };

                      // Добавляем только те детали, где есть данные
                      if (report.details && Array.isArray(report.details)) {
                        report.details.forEach((detail: any, detailIdx: number) => {
                          // Показываем только детали с данными
                          if (detailIndicesWithData.has(detailIdx)) {
                            const qty = (detail.dayQuantity || 0) + (detail.nightQuantity || 0) - (detail.wasteQuantity || 0);
                            const price = detail.price || (workerSalary?.pricings?.[detailIdx]?.perUnit || 0);
                            row[`${detail.forName || `Деталь ${detailIdx + 1}`} (кол-во)`] = qty;
                            row[`${detail.forName || `Деталь ${detailIdx + 1}`} (цена)`] = price;
                            row[`${detail.forName || `Деталь ${detailIdx + 1}`} (сумма)`] = (qty * price).toFixed(2);
                          }
                        });
                      }
                      reportData.push(row);
                    });

                    // Подготовка данных для транзакций
                    const transactionData: any[] = [];
                    workerTransactions.forEach((tx: any, idx: number) => {
                      const txCur = tx.currency || tx.salaryCurrency || 'USD';
                      const converted = convertToReportCurrency(tx.amount || 0, txCur, workerCurrency, tx.exchangeRate || exchangeRate || 1);
                      transactionData.push({
                        '№': idx + 1,
                        'Дата': tx.date || (tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : ''),
                        'Тип': tx.expenseType === ExpenseType.BONUS ? 'Премия' : 'Оплата',
                        'Сумма (оригинал)': `${tx.amount || 0} ${txCur}`,
                        'Сумма (в валюте зарплаты)': converted.toFixed(2),
                        'Валюта': txCur,
                        'Описание': tx.description || '',
                      });
                    });

                    // Добавляем итоги
                    const totalEarned = workerReports.reduce((acc: number, report: any) => {
                      const reportSum = report.totalPrice || report.total || report.sum || report.amount || 0;
                      return acc + reportSum;
                    }, 0);

                    const payments = workerTransactions.filter((tx: any) => tx.expenseType !== ExpenseType.BONUS);
                    const bonuses = workerTransactions.filter((tx: any) => tx.expenseType === ExpenseType.BONUS);

                    const totalPayments = payments.reduce((acc: number, tx: any) => {
                      const txCur = tx.currency || tx.salaryCurrency || 'USD';
                      return acc + convertToReportCurrency(tx.amount || 0, txCur, workerCurrency, tx.exchangeRate || exchangeRate || 1);
                    }, 0);

                    const totalBonuses = bonuses.reduce((acc: number, tx: any) => {
                      const txCur = tx.currency || tx.salaryCurrency || 'USD';
                      return acc + convertToReportCurrency(tx.amount || 0, txCur, workerCurrency, tx.exchangeRate || exchangeRate || 1);
                    }, 0);

                    // Создаем один лист для каждого сотрудника со всеми данными
                    const workerName = workerData.workerName || '';
                    const workerSurname = workerSalary?.workerSurname || workerData.workerSurname || '';
                    const fullName = workerSurname 
                      ? `${workerName} ${workerSurname}`.trim()
                      : workerName || `Сотрудник ${workerIdx + 1}`;
                    const sheetName = getUniqueSheetName(fullName);
                    
                    // Создаем лист и добавляем данные построчно с явным указанием позиций
                    const workerSheet: any = {};
                    let currentRow = 0;
                    
                    // Определяем порядок колонок для отчетов
                    let reportColumnOrder: string[] = [];
                    if (reportData.length > 0) {
                      reportColumnOrder = Object.keys(reportData[0]);
                    }
                    
                    // Определяем порядок колонок для транзакций
                    let txColumnOrder: string[] = [];
                    if (transactionData.length > 0) {
                      txColumnOrder = Object.keys(transactionData[0]);
                    }
                    
                    // Функция для добавления ячейки
                    const setCell = (row: number, col: number, value: any) => {
                      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                      workerSheet[cellAddress] = { v: value, t: typeof value === 'number' ? 'n' : 's' };
                    };
                    
                    // Добавляем заголовок для отчетов
                    if (reportData.length > 0) {
                      setCell(currentRow, 0, 'ОТЧЕТЫ');
                      currentRow += 2;
                      
                      // Добавляем заголовки отчетов
                      reportColumnOrder.forEach((key, colIndex) => {
                        setCell(currentRow, colIndex, key);
                      });
                      currentRow++;
                      
                      // Добавляем данные отчетов
                      reportData.forEach(row => {
                        reportColumnOrder.forEach((key, colIndex) => {
                          setCell(currentRow, colIndex, row[key] || '');
                        });
                        currentRow++;
                      });
                      currentRow++; // Пустая строка
                    }
                    
                    // Добавляем заголовок для транзакций (начинаем с колонки A)
                    if (transactionData.length > 0) {
                      setCell(currentRow, 0, 'ТРАНЗАКЦИИ');
                      currentRow += 2;
                      
                      // Добавляем заголовки транзакций
                      txColumnOrder.forEach((key, colIndex) => {
                        setCell(currentRow, colIndex, key);
                      });
                      currentRow++;
                      
                      // Добавляем данные транзакций
                      transactionData.forEach(row => {
                        txColumnOrder.forEach((key, colIndex) => {
                          setCell(currentRow, colIndex, row[key] || '');
                        });
                        currentRow++;
                      });
                      currentRow++; // Пустая строка
                    }
                    
                    // Добавляем заголовок для итогов (начинаем с колонки A)
                    setCell(currentRow, 0, 'ИТОГИ');
                    currentRow += 2;
                    
                    // Добавляем итоги
                    const summaryData = workerSalary?.salaryType === SalaryType.PERMONTH
                      ? [
                          { 'Показатель': 'Зарплата за месяц', 'Значение': (workerSalary?.pricings?.reduce((acc: number, pricing: any) => acc + (pricing.perMonth || 0), 0) || 0).toFixed(2) + ' ' + workerCurrency },
                          { 'Показатель': 'Оплаты', 'Значение': totalPayments.toFixed(2) + ' ' + workerCurrency },
                          { 'Показатель': 'Премии', 'Значение': totalBonuses.toFixed(2) + ' ' + workerCurrency },
                        ]
                      : [
                          { 'Показатель': 'Общ заработано', 'Значение': totalEarned.toFixed(2) + ' ' + workerCurrency },
                          { 'Показатель': 'Оплаты', 'Значение': totalPayments.toFixed(2) + ' ' + workerCurrency },
                          { 'Показатель': 'Премии', 'Значение': totalBonuses.toFixed(2) + ' ' + workerCurrency },
                          { 'Показатель': 'Надо заплатить', 'Значение': (totalEarned - totalPayments).toFixed(2) + ' ' + workerCurrency },
                        ];
                    
                    summaryData.forEach(row => {
                      setCell(currentRow, 0, row['Показатель']);
                      setCell(currentRow, 1, row['Значение']);
                      currentRow++;
                    });
                    
                    // Устанавливаем диапазон листа
                    workerSheet['!ref'] = XLSX.utils.encode_range({
                      s: { r: 0, c: 0 },
                      e: { r: currentRow - 1, c: Math.max(reportColumnOrder.length, txColumnOrder.length, 2) - 1 }
                    });
                    
                    // Автоподбор ширины колонок
                    const maxWidth = 50;
                    const minWidth = 10;
                    const maxCol = Math.max(reportColumnOrder.length, txColumnOrder.length, 2);
                    const colWidths: any[] = [];
                    
                    // Вычисляем ширину для каждой колонки
                    for (let col = 0; col < maxCol; col++) {
                      let maxLen = 0;
                      
                      // Проверяем заголовки отчетов
                      if (col < reportColumnOrder.length && reportData.length > 0) {
                        maxLen = Math.max(maxLen, reportColumnOrder[col].length);
                        reportData.forEach(row => {
                          const val = row[reportColumnOrder[col]];
                          if (val !== undefined && val !== null) {
                            maxLen = Math.max(maxLen, String(val).length);
                          }
                        });
                      }
                      
                      // Проверяем заголовки транзакций
                      if (col < txColumnOrder.length && transactionData.length > 0) {
                        maxLen = Math.max(maxLen, txColumnOrder[col].length);
                        transactionData.forEach(row => {
                          const val = row[txColumnOrder[col]];
                          if (val !== undefined && val !== null) {
                            maxLen = Math.max(maxLen, String(val).length);
                          }
                        });
                      }
                      
                      // Проверяем итоги (колонки 0 и 1)
                      if (col < 2) {
                        summaryData.forEach(row => {
                          const val = col === 0 ? row['Показатель'] : row['Значение'];
                          if (val !== undefined && val !== null) {
                            maxLen = Math.max(maxLen, String(val).length);
                          }
                        });
                      }
                      
                      colWidths.push({
                        wch: Math.min(Math.max(maxLen + 2, minWidth), maxWidth)
                      });
                    }
                    
                    workerSheet['!cols'] = colWidths;
                    
                    XLSX.utils.book_append_sheet(workbook, workerSheet, sheetName);
                  });

                  // Сохраняем файл
                  const fileName = `salary_report_all_workers_${allWorkersReportDateRange?.[0] || 'start'}_${allWorkersReportDateRange?.[1] || 'end'}.xlsx`;
                  XLSX.writeFile(workbook, fileName);
                  message.success('Отчет успешно экспортирован');
                }}
              >
                Экспорт в Excel
              </Button>
            </div>
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {(allWorkersSearchTerm
                ? allWorkersData.filter((w) =>
                    w.workerName?.toLowerCase().includes(allWorkersSearchTerm.toLowerCase())
                  )
                : allWorkersData
              ).map((workerData, workerIndex) => {
              const workerSalary = workerData.salary;
              const workerReports = workerData.reports || [];
              const workerTransactions = workerData.transactions || [];
              const workerCurrency = workerSalary?.salaryCurrency || 'USD';
              
              // Получаем yukPrice для этого сотрудника
              const workerYukPrice = workerCurrency === 'USD' ? yukPriceUSD : yukPriceUZS;
              
              // Создаем колонки для деталей этого сотрудника
              let workerDetailColumns: any[] = [];
              if (Array.isArray(workerReports) && workerReports.length && workerReports[0].details) {
                const shownDetails = (workerReports[0].details as any[])
                  .map((detail: any, idx: number) => {
                    const hasQty = workerReports.some((r: any) => {
                      const d = r.details[idx];
                      return d && ((d.dayQuantity || 0) + (d.nightQuantity || 0) - (d.wasteQuantity || 0)) !== 0;
                    });
                    return hasQty ? { ...detail, idx } : null;
                  })
                  .filter(Boolean);
                workerDetailColumns = shownDetails.map((detail: any) => ({
                  title: `${detail.forName}`,
                  children: [
                    {
                      title: 'Кол-во',
                      key: `qty-${detail.idx}`,
                      width: 90,
                      render: (_: any, record: any) => {
                        const d = record.details[detail.idx];
                        return d ? (d.dayQuantity || 0) + (d.nightQuantity || 0) - (d.wasteQuantity || 0) : 0;
                      },
                    },
                    {
                      title: 'Цена',
                      key: `price-${detail.idx}`,
                      width: 90,
                      render: (_: any, record: any) => {
                        const d = record.details[detail.idx];
                        return d ? d.price || (workerSalary?.pricings?.[detail.idx]?.perUnit || 0) : 0;
                      },
                    },
                    {
                      title: 'Сумма',
                      key: `sum-${detail.idx}`,
                      width: 90,
                      render: (_: any, record: any) => {
                        const d = record.details[detail.idx];
                        const price = d ? d.price || (workerSalary?.pricings?.[detail.idx]?.perUnit || 0) : 0;
                        const qty = d ? (d.dayQuantity || 0) + (d.nightQuantity || 0) - (d.wasteQuantity || 0) : 0;
                        return (qty * price).toLocaleString(undefined, { maximumFractionDigits: 2 });
                      },
                    },
                  ],
                }));
              }

              const workerReportColumns = [
                { 
                  title: '№', 
                  key: 'index', 
                  fixed: 'left' as 'left', 
                  width: 60,
                  render: (_: any, __: any, index: number) => index + 1
                },
                { title: 'Дата', dataIndex: 'date', key: 'date', fixed: 'left' as 'left', width: 100 },
                ...workerDetailColumns,
                {
                  title: 'ЮК',
                  children: [
                    { title: 'кол', dataIndex: 'yuk', key: 'yuk', width: 80 },
                    { title: 'сумма', key: 'yukSum', width: 120, render: (_: any, record: any) => ((record.yuk || 0) * workerYukPrice).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' ' + workerCurrency },
                  ],
                },
                {
                  title: 'Сумма за день',
                  dataIndex: 'totalPrice',
                  key: 'totalPrice',
                  width: 120,
                  render: (value: any) => value ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' ' + workerCurrency : '0 ' + workerCurrency,
                },
              ];

              const totalEarned = workerReports.reduce((acc: number, report: any) => {
                const reportSum = report.totalPrice || report.total || report.sum || report.amount || 0;
                return acc + reportSum;
              }, 0);

              const payments = workerTransactions.filter((tx: any) => tx.expenseType !== ExpenseType.BONUS);
              const bonuses = workerTransactions.filter((tx: any) => tx.expenseType === ExpenseType.BONUS);

              const totalPayments = payments.reduce((acc: number, tx: any) => {
                const txCur = tx.currency || tx.salaryCurrency || 'USD';
                return acc + convertToReportCurrency(tx.amount || 0, txCur, workerCurrency, tx.exchangeRate || exchangeRate || 1);
              }, 0);

              const totalBonuses = bonuses.reduce((acc: number, tx: any) => {
                const txCur = tx.currency || tx.salaryCurrency || 'USD';
                return acc + convertToReportCurrency(tx.amount || 0, txCur, workerCurrency, tx.exchangeRate || exchangeRate || 1);
              }, 0);

              const workerIdStr = (workerData.workerId || workerIndex).toString();
              
              return (
                <div key={workerData.workerId || workerIndex} style={{ marginBottom: 32, border: '1px solid #d9d9d9', padding: 16, borderRadius: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={4} style={{ margin: 0 }}>
                      {workerData.workerName || 'Неизвестный сотрудник'}
                      {workerSalary && ` (${workerSalary.salaryType}, ${workerCurrency})`}
                    </Title>
                    <Button
                      type="default"
                      onClick={() => {
                        const printWindow = window.open('', '_blank');
                        if (!printWindow) return;
                        
                        // Получаем HTML элементы для этого сотрудника
                        const workerDiv = document.getElementById(`worker-report-${workerIdStr}`);
                        if (!workerDiv) return;
                        
                        const reportTable = workerDiv.querySelector(`#worker-report-table-${workerIdStr}`)?.outerHTML || '';
                        const txTable = workerDiv.querySelector(`#worker-tx-table-${workerIdStr}`)?.outerHTML || '';
                        const summaryDiv = workerDiv.querySelector(`#worker-summary-${workerIdStr}`)?.outerHTML || '';
                        
                        printWindow.document.write(`
                          <html>
                            <head>
                              <title>Печать отчета: ${workerData.workerName}</title>
                              <style>
                                body, table, th, td { font-family: Arial, sans-serif; font-size: 10px !important; padding: 4px !important; margin: 0 !important; border: 1px solid #000 !important; }
                                th { background: #f5f5f5; font-weight: bold; }
                                h2 { margin: 8px 0; font-size: 14px; }
                                h3 { margin: 8px 0; font-size: 12px; }
                                h4 { margin: 8px 0; font-size: 11px; }
                                .summary { margin-top: 16px; padding: 8px; background: #f5f5f5; }
                              </style>
                            </head>
                            <body>
                              <h2>Отчет: ${workerData.workerName || 'Неизвестный сотрудник'}</h2>
                              <div style="font-size:10px; margin-bottom:8px;">${formatPeriod(allWorkersReportDateRange)}</div>
                              ${reportTable}
                              ${txTable ? `<h3>Транзакции</h3>${txTable}` : ''}
                              <div class="summary">${summaryDiv}</div>
                            </body>
                          </html>
                        `);
                        printWindow.document.close();
                        printWindow.focus();
                        setTimeout(() => {
                          printWindow.print();
                        }, 250);
                      }}
                    >
                      Печать
                    </Button>
                  </div>
                  
                  <div id={`worker-report-${workerIdStr}`}>
                  {workerReports.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <Table
                        id={`worker-report-table-${workerIdStr}`}
                        dataSource={workerReports.map((report: any, idx: number) => ({ ...report, key: report._id || idx }))}
                        pagination={false}
                        bordered
                        scroll={{ x: true }}
                        columns={workerReportColumns}
                        size="small"
                      />
                    </div>
                  )}

                  {workerTransactions.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ marginBottom: 8 }}>Транзакции</h4>
                      <Table
                        id={`worker-tx-table-${workerIdStr}`}
                        dataSource={workerTransactions.map((tx: any, idx: number) => ({ ...tx, key: tx._id || idx }))}
                        pagination={false}
                        bordered
                        columns={[
                          { 
                            title: '№', 
                            key: 'index', 
                            width: 60,
                            render: (_: any, __: any, index: number) => index + 1
                          },
                          { title: 'Дата', dataIndex: 'date', key: 'date', render: (v, r) => v || (r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '') },
                          {
                            title: 'Тип',
                            dataIndex: 'expenseType',
                            key: 'expenseType',
                            render: (v) => v === ExpenseType.BONUS ? 'Премия' : 'Оплата'
                          },
                          {
                            title: 'Сумма',
                            dataIndex: 'amount',
                            key: 'amount',
                            render: (v, r) => {
                              const txCur = r.currency || r.salaryCurrency || 'USD';
                              if (txCur === workerCurrency) return v;
                              const converted = convertToReportCurrency(v, txCur, workerCurrency, r.exchangeRate || exchangeRate || 1);
                              return `${v} ${txCur} (${converted.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${workerCurrency})`;
                            }
                          },
                          { title: 'Валюта', dataIndex: 'currency', key: 'currency', render: (v, r) => v || r.salaryCurrency || workerCurrency },
                          { title: 'Описание', dataIndex: 'description', key: 'description' },
                        ]}
                        size="small"
                      />
                    </div>
                  )}

                  <div id={`worker-summary-${workerIdStr}`} style={{ marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                    {workerSalary?.salaryType === SalaryType.PERMONTH ? (
                      <>
                        <div>Зарплата за месяц: <b>{(workerSalary?.pricings?.reduce((acc: number, pricing: any) => acc + (pricing.perMonth || 0), 0) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {workerCurrency}</b></div>
                        <div>Оплаты: <b>{totalPayments.toLocaleString(undefined, { maximumFractionDigits: 2 })} {workerCurrency}</b></div>
                        <div>Премии: <b>{totalBonuses.toLocaleString(undefined, { maximumFractionDigits: 2 })} {workerCurrency}</b></div>
                      </>
                    ) : (
                      <>
                        <div>Общ заработано: <b>{totalEarned.toLocaleString(undefined, { maximumFractionDigits: 2 })} {workerCurrency}</b></div>
                        <div>Оплаты: <b>{totalPayments.toLocaleString(undefined, { maximumFractionDigits: 2 })} {workerCurrency}</b></div>
                        <div>Премии: <b>{totalBonuses.toLocaleString(undefined, { maximumFractionDigits: 2 })} {workerCurrency}</b></div>
                        <div>Надо заплатить: <b>{(totalEarned - totalPayments).toLocaleString(undefined, { maximumFractionDigits: 2 })} {workerCurrency}</b></div>
                      </>
                    )}
                  </div>
                  </div>
                </div>
              );
              })}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
